import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowLeft, Eye, MapPin, Bell, Database, UserX, Globe } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";

const PrivacyPage = () => {
  const [settings, setSettings] = useState({
    profile_visibility: true,
    location_sharing: true,
    marketing: false,
    data_collection: true,
    third_party: false,
    search_visible: true,
  });

  const toggle = (key) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
    toast.success("Privacy setting updated");
  };

  const privacyItems = [
    { key: "profile_visibility", icon: Eye, label: "Profile Visibility", desc: "Allow customers to see your profile, name, and rating before booking" },
    { key: "location_sharing", icon: MapPin, label: "Location Sharing", desc: "Share your live location with customers during active jobs. Required for live tracking." },
    { key: "search_visible", icon: Globe, label: "Appear in Search", desc: "Show your profile in nearby professional search results on booking pages" },
    { key: "marketing", icon: Bell, label: "Marketing Communications", desc: "Receive promotional emails, newsletters, and special offers from Stack" },
    { key: "data_collection", icon: Database, label: "Analytics & Improvement", desc: "Allow Stack to use anonymized usage data to improve matching and app experience" },
    { key: "third_party", icon: UserX, label: "Third-Party Data Sharing", desc: "Allow Stack to share anonymized data with trusted partners for service improvement" },
  ];

  return (
    <div className="min-h-screen bg-stone-50" data-testid="privacy-page">
      <Navbar />
      <div className="pt-24 pb-16 px-4 max-w-3xl mx-auto">
        <Link to="/professional/dashboard" className="inline-flex items-center gap-2 text-stone-500 hover:text-emerald-950 mb-6 transition-colors text-sm" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Work Hub
        </Link>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-2xl font-bold text-emerald-950 flex items-center gap-2 mb-2">
            <Lock className="w-6 h-6 text-violet-600" /> Privacy Settings
          </h1>
          <p className="text-stone-500 text-sm mb-6">Control how your data is used and shared across the Stack platform.</p>

          <div className="space-y-3">
            {privacyItems.map((item) => (
              <div key={item.key} className="bg-white rounded-2xl border border-stone-100 p-5 flex items-center justify-between" data-testid={`privacy-${item.key}`}>
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-9 h-9 bg-stone-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                    <item.icon className="w-4 h-4 text-stone-600" />
                  </div>
                  <div className="min-w-0">
                    <Label className="font-medium text-emerald-950">{item.label}</Label>
                    <p className="text-xs text-stone-500 mt-0.5">{item.desc}</p>
                  </div>
                </div>
                <Switch checked={settings[item.key]} onCheckedChange={() => toggle(item.key)} className="shrink-0 ml-3" data-testid={`toggle-${item.key}`} />
              </div>
            ))}
          </div>

          <div className="mt-6 bg-violet-50 border border-violet-200 rounded-xl p-4 text-sm text-violet-800" data-testid="privacy-notice">
            <p className="font-medium mb-1">Your Privacy Matters</p>
            <p className="text-violet-600">Stack is committed to protecting your personal data. Location sharing is only active during jobs. For more details, see our <Link to="/terms" className="underline font-medium">Terms of Service</Link>.</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPage;
